import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiCalendar, FiMapPin, FiPlus, FiTrash2,
  FiX, FiLink, FiImage, FiUserCheck
} from 'react-icons/fi';
import { db } from './firebase';
import {
  collection, getDocs, addDoc, deleteDoc, doc,
  orderBy, query, serverTimestamp, updateDoc, where
} from 'firebase/firestore';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Footer from './Footer';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

/* ── COUNTDOWN ───────────────────────────────────────────────────── */
const Countdown = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState({});
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    const calc = () => {
      const diff = new Date(targetDate) - new Date();
      if (diff <= 0) { setExpired(true); return; }
      setTimeLeft({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff % 86400000) / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    calc();
    const t = setInterval(calc, 1000);
    return () => clearInterval(t);
  }, [targetDate]);

  if (expired) return <span style={{ fontSize: 12, color: '#ef4444' }}>Registration Closed</span>;

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      {[['d', 'DAYS'], ['h', 'HRS'], ['m', 'MINS'], ['s', 'SECS']].map(([k, lbl]) => (
        <div key={k} style={{
          textAlign: 'center',
          background: 'rgba(var(--accent-rgb, 34,197,94),0.05)',
          padding: '6px 10px',
          borderRadius: 12,
          border: '1px solid rgba(var(--accent-rgb, 34,197,94),0.1)',
          minWidth: 46
        }}>
          <div style={{ fontSize: 16, fontWeight: 900, color: 'var(--accent)' }}>
            {String(timeLeft[k] || 0).padStart(2, '0')}
          </div>
          <div style={{ fontSize: 8, fontWeight: 800, color: 'var(--text-muted)', marginTop: 2 }}>{lbl}</div>
        </div>
      ))}
    </div>
  );
};

/* ── EVENT CARD ──────────────────────────────────────────────────── */
const EventCard = ({ event, isAdmin, onDelete, onEdit, onParticipate, c, isDarkMode }) => {
  const regDate = event.lastRegistrationDate ? new Date(event.lastRegistrationDate) : null;
  const eventDate = event.date ? new Date(event.date) : null;
  const isPast = eventDate && eventDate < new Date();
  const canReg = regDate && regDate > new Date();

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      whileHover={{ y: -6, boxShadow: isDarkMode ? '0 30px 60px rgba(0,0,0,0.8)' : '0 20px 40px rgba(0,0,0,0.1)' }}
      style={{
        background: c.card,
        border: `1px solid ${c.border}`,
        borderRadius: 24,
        overflow: 'hidden',
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div style={{ height: 200, overflow: 'hidden', position: 'relative', background: 'var(--bg-surface)' }}>
        {event.thumbnail ? (
          <img
            src={event.thumbnail}
            alt={event.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.6s cubic-bezier(0.16,1,0.3,1)' }}
            onMouseEnter={e => { e.target.style.transform = 'scale(1.1)'; }}
            onMouseLeave={e => { e.target.style.transform = 'scale(1)'; }}
          />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.2 }}>
            <FiCalendar size={48} />
          </div>
        )}

        <div style={{
          position: 'absolute', top: 16, left: 16,
          padding: '6px 12px', borderRadius: 50, fontSize: 10, fontWeight: 800,
          background: isPast ? 'rgba(239, 68, 68, 0.1)' : 'rgba(var(--accent-rgb, 34, 197, 94), 0.1)',
          color: isPast ? '#ef4444' : 'var(--accent)',
          border: `1px solid ${isPast ? 'rgba(239, 68, 68, 0.2)' : 'rgba(var(--accent-rgb, 34, 197, 94), 0.2)'}`,
          backdropFilter: 'blur(12px)',
          textTransform: 'uppercase', letterSpacing: '0.05em'
        }}>
          {isPast ? 'Archived' : 'Live Now'}
        </div>
      </div>

      <div style={{ padding: 24, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <h3 style={{ fontSize: 18, fontWeight: 800, color: c.text, marginBottom: 12, lineHeight: 1.3 }}>{event.title}</h3>
        {event.description && (
          <p style={{ fontSize: 13, color: c.muted, lineHeight: 1.6, marginBottom: 20 }}>
            {event.description.length > 100 ? event.description.slice(0, 100) + '…' : event.description}
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
          {eventDate && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: c.muted }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(var(--accent-rgb, 34,197,94),0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FiCalendar size={14} style={{ color: 'var(--accent)' }} />
              </div>
              {eventDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          )}
          {event.venue && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: c.muted }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: 'rgba(16,185,129,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FiMapPin size={14} style={{ color: '#10b981' }} />
              </div>
              {event.venue}
            </div>
          )}
        </div>

        {!isPast && regDate && (
          <div style={{ marginBottom: 24, background: 'var(--bg-surface)', padding: 12, borderRadius: 16, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 10, color: c.muted, marginBottom: 8, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Registration Ends In</div>
            <Countdown targetDate={event.lastRegistrationDate} />
          </div>
        )}

        <div style={{ marginTop: 'auto', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {event.registrationLink && canReg && (
            <motion.a
              href={event.registrationLink} target="_blank" rel="noopener noreferrer"
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              style={{
                flex: 1, minWidth: 100, height: 44, background: 'var(--accent)', borderRadius: 14,
                color: '#000', fontWeight: 800, fontSize: 13, textDecoration: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                boxShadow: '0 8px 16px rgba(var(--accent-rgb, 34,197,94),0.2)'
              }}
            >
              <FiLink size={14} /> Register
            </motion.a>
          )}

          {event.isParticipationEnabled && !isPast && (
            <motion.button
              whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
              onClick={() => onParticipate(event)}
              style={{
                flex: 1, minWidth: 100, height: 44, background: 'var(--bg-surface)',
                border: '1px solid var(--border)', color: 'var(--text-main)', borderRadius: 14,
                fontWeight: 800, fontSize: 13, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <FiUserCheck size={14} /> Participate
            </motion.button>
          )}

          {isAdmin && (
            <div style={{ display: 'flex', gap: 10, width: '100%' }}>
              <motion.button
                whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                onClick={() => onEdit(event)}
                style={{
                  flex: 1, height: 44, borderRadius: 14, background: 'var(--bg-surface)',
                  border: '1px solid var(--border)', color: 'var(--text-main)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >
                <FiPlus style={{ transform: 'rotate(45deg)' }} size={16} />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                onClick={() => onDelete(event.id)}
                style={{
                  flex: 1, height: 44, borderRadius: 14, background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.2)', color: '#ef4444', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >
                <FiTrash2 size={16} />
              </motion.button>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

/* ── PARTICIPANT MODAL ───────────────────────────────────────────── */
const ParticipantModal = ({ onClose, onSave, event, user, c }) => {
  const [form, setForm] = useState({
    fullName: user?.fullName || user?.displayName || '',
    email: user?.email || '',
    photo: user?.photo || user?.photoURL || '',
    uid: user?.uid || '',
    branch: '',
    year: '',
    whatsapp: ''
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.branch || !form.year || !form.whatsapp) {
      toast.error('Please fill all details');
      return;
    }
    setSaving(true);
    try {
      await onSave({ ...form, eventId: event.id, eventTitle: event.title, timestamp: serverTimestamp() });
      toast.success('Successfully registered for the event!');
      onClose();
    } catch (e) {
      toast.error('Error: ' + e.message);
    }
    setSaving(false);
  };

  const inputStyle = {
    width: '100%', height: 44, background: 'var(--bg-surface)', border: '1px solid var(--border)',
    borderRadius: 12, padding: '0 16px', color: 'var(--text-main)', fontSize: 14, outline: 'none',
    width: 'calc(100% - 32px)'
  };
  const labelStyle = { fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 6, textTransform: 'uppercase' };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, zIndex: 3001, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.9, y: 40 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 40 }}
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 32, padding: 32, width: '100%', maxWidth: 500, overflowY: 'auto' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24, alignItems: 'center' }}>
          <div>
            <h2 style={{ fontWeight: 900, fontSize: 24, color: 'var(--text-main)' }}>Participate</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>{event.title}</p>
          </div>
          <button onClick={onClose} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text-main)', cursor: 'pointer', width: 40, height: 40, borderRadius: 12 }}>
            <FiX size={20} />
          </button>
        </div>

        <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 24, background: 'var(--bg-surface)', padding: 16, borderRadius: 20, border: '1px solid var(--border)' }}>
          {form.photo ? (
            <img src={form.photo} alt="User" style={{ width: 50, height: 50, borderRadius: '50%', objectFit: 'cover' }} />
          ) : (
            <div style={{ width: 50, height: 50, borderRadius: '50%', background: 'var(--accent)', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900 }}>
              {form.fullName?.[0]}
            </div>
          )}
          <div>
            <div style={{ fontWeight: 800, color: 'var(--text-main)' }}>{form.fullName}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{form.email}</div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <label style={labelStyle}>Branch / Department *</label>
            <input placeholder="e.g. CSE, IT, ECE" value={form.branch} onChange={e => set('branch', e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Current Year *</label>
            <select value={form.year} onChange={e => set('year', e.target.value)} style={{ ...inputStyle, width: '100%' }}>
              <option value="">Select Year</option>
              <option value="1st Year">1st Year</option>
              <option value="2nd Year">2nd Year</option>
              <option value="3rd Year">3rd Year</option>
              <option value="4th Year">4th Year</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>WhatsApp Number *</label>
            <input placeholder="Enter 10-digit number" value={form.whatsapp} onChange={e => set('whatsapp', e.target.value)} style={inputStyle} />
          </div>
        </div>

        <button onClick={handleSave} disabled={saving} style={{ width: '100%', height: 50, marginTop: 32, borderRadius: 16, border: 'none', background: 'var(--accent)', color: '#000', fontWeight: 900, fontSize: 15, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Registering...' : 'Submit Entry'}
        </button>
      </motion.div>
    </motion.div>
  );
};

/* ── EVENT MODAL ─────────────────────────────────────────────────── */
const EventModal = ({ onClose, onSave, editingEvent, c }) => {
  const isEdit = !!editingEvent;
  const [form, setForm] = useState(editingEvent || {
    title: '', description: '', date: '', lastRegistrationDate: '',
    venue: '', location: '', mapLink: '', thumbnail: '', registrationLink: '',
    isParticipationEnabled: false,
  });
  const [saving, setSaving] = useState(false);
  const [imgError, setImgError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const maxSize = 1024 * 1024;
    if (file.size > maxSize) {
      setImgError(`Image too large: ${(file.size / 1024 / 1024).toFixed(2)}MB. Max 1MB.`);
      return;
    }
    setImgError('');
    const reader = new FileReader();
    reader.onloadend = () => set('thumbnail', reader.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Event Title is required'); return; }
    setSaving(true);
    try {
      await onSave(form);
      toast.success(isEdit ? 'Event updated!' : 'Event published!');
      onClose();
    } catch (e) { toast.error('Error: ' + e.message); }
    setSaving(false);
  };

  const inputStyle = {
    width: '100%', height: 44, background: 'var(--bg-surface)', border: '1px solid var(--border)',
    borderRadius: 12, padding: '0 16px', color: 'var(--text-main)', fontSize: 14, outline: 'none',
    width: 'calc(100% - 32px)'
  };
  const labelStyle = { fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 6, textTransform: 'uppercase' };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ scale: 0.9, y: 40 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 40 }}
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 32, padding: 32, width: '100%', maxWidth: 700, maxHeight: '90vh', overflowY: 'auto' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 32 }}>
          <div>
            <h2 style={{ fontWeight: 900, fontSize: 24, color: 'var(--text-main)' }}>{isEdit ? 'Edit Event' : 'New Event'}</h2>
          </div>
          <button onClick={onClose} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', color: 'var(--text-main)', cursor: 'pointer', width: 40, height: 40, borderRadius: 12 }}>
            <FiX size={20} />
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle}>Title *</label>
            <input value={form.title} onChange={e => set('title', e.target.value)} style={inputStyle} />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle}>Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} style={{ ...inputStyle, height: 80, padding: 16 }} />
          </div>
          <div><label style={labelStyle}>Date</label><input type="date" value={form.date} onChange={e => set('date', e.target.value)} style={inputStyle} /></div>
          <div><label style={labelStyle}>Deadline</label><input type="date" value={form.lastRegistrationDate} onChange={e => set('lastRegistrationDate', e.target.value)} style={inputStyle} /></div>
          <div><label style={labelStyle}>Venue</label><input value={form.venue} onChange={e => set('venue', e.target.value)} style={inputStyle} /></div>
          <div><label style={labelStyle}>Location</label><input value={form.location} onChange={e => set('location', e.target.value)} style={inputStyle} /></div>

          <div style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center', gap: 12, background: 'var(--bg-surface)', padding: '12px 16px', borderRadius: 16, border: '1px solid var(--border)' }}>
            <input
              type="checkbox"
              checked={form.isParticipationEnabled}
              onChange={e => set('isParticipationEnabled', e.target.checked)}
              id="isParticipationEnabled"
              style={{ width: 18, height: 18, cursor: 'pointer' }}
            />
            <label htmlFor="isParticipationEnabled" style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-main)', cursor: 'pointer' }}>Enable Participation Form</label>
          </div>

          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle}>Thumbnail</label>
            <div style={{ border: '2px dashed var(--border)', borderRadius: 20, padding: 20, textAlign: 'center' }}>
              {form.thumbnail ? (
                <div style={{ position: 'relative', height: 160 }}><img src={form.thumbnail} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12 }} /><button onClick={() => set('thumbnail', '')} style={{ position: 'absolute', top: 8, right: 8, background: '#ef4444', borderRadius: '50%', border: 'none', color: '#fff', padding: 4 }}><FiX size={14} /></button></div>
              ) : (
                <label style={{ cursor: 'pointer' }}><FiImage size={24} style={{ mb: 8 }} /><div style={{ fontSize: 12, fontWeight: 700 }}>Upload Image (Max 1MB)</div><input type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} /></label>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, marginTop: 32 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 14, borderRadius: 16, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-main)', cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving || !!imgError} style={{ flex: 1, padding: 14, borderRadius: 16, border: 'none', background: 'var(--accent)', color: '#000', fontWeight: 900, cursor: 'pointer', opacity: (saving || imgError) ? 0.6 : 1 }}>{saving ? 'Wait...' : 'Save Event'}</button>
        </div>
      </motion.div>
    </motion.div>
  );
};

/* ── MAIN COMPONENT ──────────────────────────────────────────────── */
const EventComponent = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalMode, setModalMode] = useState(null); // 'add' | 'edit' | 'participate'
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filter, setFilter] = useState('all');
  const { isDarkMode } = useTheme();
  const { isAdmin, user } = useAuth();

  const c = { bg: 'var(--bg-main)', card: 'var(--bg-card)', border: 'var(--border)', text: 'var(--text-main)', muted: 'var(--text-muted)', accent: 'var(--accent)' };

  const fetchEvents = async () => {
    try {
      const q = query(collection(db, 'events'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      setEvents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch { setEvents([]); }
    setLoading(false);
  };

  useEffect(() => { fetchEvents(); }, []);

  const handleSave = async (data) => {
    if (modalMode === 'edit') {
      const { id, ...updateData } = data;
      await updateDoc(doc(db, 'events', id), { ...updateData, updatedAt: serverTimestamp() });
      setEvents(prev => prev.map(e => e.id === id ? { id, ...updateData } : e));
    } else {
      const ref = await addDoc(collection(db, 'events'), { ...data, createdAt: serverTimestamp() });
      setEvents(prev => [{ id: ref.id, ...data }, ...prev]);
    }
  };

  const handleParticipate = async (data) => {
    await addDoc(collection(db, 'event_participants'), data);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete event and clear data?')) return;
    try {
      await deleteDoc(doc(db, 'events', id));

      // Cleanup participants
      const pSnap = await getDocs(query(collection(db, 'event_participants'), where('eventId', '==', id)));
      const deletePromises = pSnap.docs.map(d => deleteDoc(doc(db, 'event_participants', d.id)));
      await Promise.all(deletePromises);

      setEvents(prev => prev.filter(e => e.id !== id));
      toast.success('Event and participant data cleared.');
    } catch (e) {
      console.error("Error deleting event:", e);
      toast.error('Failed to delete event data');
    }
  };

  const openParticipate = (event) => {
    if (!user) {
      toast.info('Please login to participate');
      return;
    }
    setSelectedEvent(event);
    setModalMode('participate');
  };

  const now = new Date();
  const filtered = events.filter(e => {
    const d = e.date ? new Date(e.date) : null;
    if (filter === 'upcoming') return d ? d >= now : true;
    if (filter === 'past') return d ? d < now : false;
    return true;
  });

  return (
    <div style={{ background: c.bg, minHeight: '100vh', padding: '100px 24px' }}>
      <ToastContainer position="top-right" theme={isDarkMode ? 'dark' : 'light'} />

      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 20 }}>
          <div>
            <h1 style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 900, color: c.text, letterSpacing: '-0.05em' }}>SnapED <span style={{ color: c.accent }}>Events</span></h1>
            <p style={{ color: c.muted, fontSize: 15, marginTop: 8 }}>Official workshops and hackathons at IPU.</p>
          </div>
          {isAdmin && (
            <button onClick={() => { setSelectedEvent(null); setModalMode('add'); }} style={{ padding: '12px 24px', background: c.accent, border: 'none', borderRadius: 14, fontWeight: 800, cursor: 'pointer' }}>+ New Event</button>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, margin: '40px 0', borderBottom: `1px solid ${c.border}`, paddingBottom: 16 }}>
          {['all', 'upcoming', 'past'].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{ background: filter === f ? c.accent : 'transparent', border: 'none', padding: '8px 16px', borderRadius: 8, color: filter === f ? '#000' : c.muted, fontWeight: 800, cursor: 'pointer' }}>{f.toUpperCase()}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 24 }}>
            {[1, 2, 3].map(i => <div key={i} style={{ height: 300, background: c.card, borderRadius: 24, animation: 'pulse 1.5s infinite' }} />)}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 24 }}>
            <AnimatePresence>
              {filtered.map(e => (
                <EventCard
                  key={e.id}
                  event={e}
                  isAdmin={isAdmin}
                  onDelete={handleDelete}
                  onEdit={(ev) => { setSelectedEvent(ev); setModalMode('edit'); }}
                  onParticipate={openParticipate}
                  isDarkMode={isDarkMode}
                  c={c}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      <AnimatePresence>
        {(modalMode === 'add' || modalMode === 'edit') && (
          <EventModal onClose={() => setModalMode(null)} onSave={handleSave} editingEvent={selectedEvent} c={c} />
        )}
        {modalMode === 'participate' && (
          <ParticipantModal onClose={() => setModalMode(null)} onSave={handleParticipate} event={selectedEvent} user={user} c={c} />
        )}
      </AnimatePresence>

      <Footer />
      <style>{`@keyframes pulse { 0%,100%{opacity:0.8} 50%{opacity:0.4} }`}</style>
    </div>
  );
};

export default EventComponent;
